// Slice 42 — Feedback inbox. Two-pane layout per the handoff: left rail
// is the scrollable list of `/admin/feedback` rows (cursor-paginated);
// right pane is the selected row's detail (with screenshot, system-info
// meta grid, GDPR redaction strip, reply composer).
//
// State coordination:
//   - status / tag filter chips re-fetch the rail with reset cursor
//   - PATCH on status-change or tag-edit updates the row in-place
//   - POST /reply / /redact bump refreshTick so the rail re-counts

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  FEEDBACK_STATUSES,
  FeedbackApi,
  type FeedbackDetail,
  type FeedbackRow,
  type FeedbackStatus,
  apiPaged,
} from "../../api/client";
import { useFeedbackEvents } from "../../api/use-feedback-events";
import { useShell } from "../../shell/HqShellContext";
import { HqAvatar, HqBadge, HqButton, HqCard, HqInput, HqStatusDot, Icons } from "../../ui";
import { MonoId } from "../licenses/MonoId";
import { FeedbackDetailPane } from "./FeedbackDetailPane";
import { clamp, fmtRelative } from "./format";

const STATUS_LABEL: Record<FeedbackStatus, string> = {
  new: "New",
  triaged: "Triaged",
  in_progress: "In progress",
  closed: "Closed",
};

type StatusFilter = "all" | FeedbackStatus;
const STATUS_FILTERS: { value: StatusFilter; label: string }[] = [
  { value: "all", label: "All" },
  ...FEEDBACK_STATUSES.map((s) => ({ value: s as StatusFilter, label: STATUS_LABEL[s] })),
];

export function FeedbackScreen() {
  const { refreshTick, bumpRefresh, toast } = useShell();
  const [rows, setRows] = useState<FeedbackRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState<StatusFilter>("all");
  const [tag, setTag] = useState("");
  const [nextCursor, setNextCursor] = useState<string | null>(null);

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<FeedbackDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // The effect re-fires on filter / refresh changes and uses the current
  // selectedId via a ref (not a dep) so picking a new row doesn't re-fetch.
  const selectedRef = useRef<string | null>(null);
  selectedRef.current = selectedId;
  useEffect(() => {
    void refreshTick;
    let cancelled = false;
    setLoading(true);
    setErr(null);
    void (async () => {
      const res = await FeedbackApi.list({
        limit: 50,
        status: status === "all" ? undefined : status,
        tag: tag.trim() || undefined,
      });
      if (cancelled) return;
      setLoading(false);
      if (!res.ok) {
        setErr(res.error.message);
        return;
      }
      setRows(res.data.data);
      setNextCursor(res.data.page.cursor);
      const sel = selectedRef.current;
      if (sel && !res.data.data.some((r) => r.id === sel)) {
        setSelectedId(null);
        setDetail(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [status, tag, refreshTick]);

  const loadMore = useCallback(async () => {
    if (!nextCursor) return;
    const res = await apiPaged<FeedbackRow>(
      `/admin/feedback?limit=50&cursor=${encodeURIComponent(nextCursor)}${
        status === "all" ? "" : `&status=${status}`
      }${tag ? `&tag=${encodeURIComponent(tag)}` : ""}`,
    );
    if (!res.ok) {
      toast(res.error.message, "danger");
      return;
    }
    setRows((prev) => [...prev, ...res.data.data]);
    setNextCursor(res.data.page.cursor);
  }, [nextCursor, status, tag, toast]);

  const select = useCallback(async (id: string) => {
    setSelectedId(id);
    setDetail(null);
    setDetailLoading(true);
    const res = await FeedbackApi.get(id);
    setDetailLoading(false);
    if (!res.ok) {
      setDetail(null);
      return;
    }
    setDetail(res.data);
  }, []);

  // Live updates from the server bus. The handler closes over the
  // setters and `selectedRef` so the SSE subscription itself stays
  // open across renders (use-feedback-events refs the handler).
  useFeedbackEvents((event) => {
    // Any new ticket / status change / customer reply means the rail
    // is stale. Bumping refreshTick re-runs the list effect — the
    // active filter is preserved.
    if (
      event.type === "ticket.created" ||
      event.type === "ticket.updated" ||
      event.type === "message.created"
    ) {
      bumpRefresh();
    }
    // If the user has the affected ticket open, pull the fresh detail
    // so the new message / status appears without a manual refresh.
    if (
      (event.type === "message.created" ||
        event.type === "ticket.updated" ||
        event.type === "ticket.read_by_user" ||
        event.type === "ticket.read_by_staff") &&
      selectedRef.current === event.ticketId
    ) {
      void (async () => {
        const fresh = await FeedbackApi.get(event.ticketId);
        if (fresh.ok) setDetail(fresh.data);
      })();
    }
  });

  // Visible-row search (mono substring across licenseId + body) so the
  // operator can dial a row down without forcing a re-fetch — the backend
  // already filters by status+tag, search stays cheap client-side.
  const visible = useMemo(() => {
    if (!q.trim()) return rows;
    const needle = q.trim().toLowerCase();
    return rows.filter(
      (r) =>
        (r.licenseId?.toLowerCase().includes(needle) ?? false) ||
        r.body.toLowerCase().includes(needle),
    );
  }, [rows, q]);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <Header total={rows.length} />
      <FilterBar q={q} onQ={setQ} status={status} onStatus={setStatus} tag={tag} onTag={setTag} />
      <HqCard padding={false}>
        <div style={{ display: "grid", gridTemplateColumns: "380px 1fr", minHeight: 540 }}>
          <Rail
            rows={visible}
            loading={loading}
            err={err}
            selectedId={selectedId}
            onSelect={(id) => void select(id)}
            nextCursor={nextCursor}
            onLoadMore={() => void loadMore()}
          />
          <FeedbackDetailPane
            row={detail}
            loading={detailLoading}
            onUpdate={(patched) => {
              setDetail(patched);
              setRows((prev) =>
                prev.map((r) =>
                  r.id === patched.id
                    ? {
                        ...r,
                        status: patched.status,
                        tags: patched.tags,
                        body: patched.body,
                        systemInfo: patched.systemInfo,
                        redactedAt: patched.redactedAt,
                        redactedBy: patched.redactedBy,
                        redactedFields: patched.redactedFields,
                        hasScreenshot: patched.screenshotBase64 !== null,
                      }
                    : r,
                ),
              );
              bumpRefresh();
            }}
          />
        </div>
      </HqCard>
    </div>
  );
}

function Header({ total }: { total: number }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
      <Icons.Inbox width={20} height={20} sw={1.7} style={{ color: "var(--hq-accent)" }} />
      <div style={{ display: "flex", flexDirection: "column" }}>
        <h1
          style={{
            margin: 0,
            fontSize: 24,
            fontWeight: 600,
            letterSpacing: "-.02em",
            color: "var(--hq-text)",
            lineHeight: 1.2,
          }}
        >
          Feedback
        </h1>
        <span className="hq-mono" style={{ fontSize: 11, color: "var(--hq-text-dim)" }}>
          {total} in the inbox
        </span>
      </div>
      <span style={{ flex: 1 }} />
    </div>
  );
}

function FilterBar({
  q,
  onQ,
  status,
  onStatus,
  tag,
  onTag,
}: {
  q: string;
  onQ: (v: string) => void;
  status: StatusFilter;
  onStatus: (v: StatusFilter) => void;
  tag: string;
  onTag: (v: string) => void;
}) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
      <div style={{ flex: 1, maxWidth: 320 }}>
        <HqInput
          placeholder="Search body or license ID…"
          leadingIcon={Icons.Search}
          value={q}
          onChange={(e) => onQ(e.target.value)}
        />
      </div>
      <Segmented
        options={STATUS_FILTERS}
        value={status}
        onChange={(v) => onStatus(v as StatusFilter)}
      />
      <div style={{ width: 170 }}>
        <HqInput
          placeholder="Tag filter (exact)"
          leadingIcon={Icons.Tag}
          value={tag}
          onChange={(e) => onTag(e.target.value)}
        />
      </div>
    </div>
  );
}

function Segmented({
  options,
  value,
  onChange,
}: {
  options: { value: string; label: string }[];
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div
      style={{
        display: "inline-flex",
        padding: 3,
        gap: 2,
        background: "var(--hq-bg-input)",
        borderRadius: "var(--hq-radius-lg)",
        border: "1px solid var(--hq-border-input)",
      }}
    >
      {options.map((o) => {
        const active = o.value === value;
        return (
          <button
            type="button"
            key={o.value}
            onClick={() => onChange(o.value)}
            style={{
              padding: "0 10px",
              height: 26,
              borderRadius: "var(--hq-radius-md)",
              background: active ? "var(--hq-bg-active)" : "transparent",
              color: active ? "var(--hq-text)" : "var(--hq-text-muted)",
              fontSize: 11.5,
              border: "none",
              cursor: "pointer",
              fontWeight: 500,
            }}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

function Rail({
  rows,
  loading,
  err,
  selectedId,
  onSelect,
  nextCursor,
  onLoadMore,
}: {
  rows: FeedbackRow[];
  loading: boolean;
  err: string | null;
  selectedId: string | null;
  onSelect: (id: string) => void;
  nextCursor: string | null;
  onLoadMore: () => void;
}) {
  return (
    <div
      className="hq-scroll"
      style={{
        borderRight: "1px solid var(--hq-border)",
        overflowY: "auto",
        maxHeight: 720,
      }}
    >
      {err && <div style={{ padding: 24, color: "var(--hq-danger)", fontSize: 12.5 }}>{err}</div>}
      {!err && rows.length === 0 && (
        <div style={{ padding: 24, color: "var(--hq-text-dim)", fontSize: 12.5 }}>
          {loading ? "Loading…" : "Inbox empty for this filter."}
        </div>
      )}
      {rows.map((r) => (
        <RailRow key={r.id} row={r} active={r.id === selectedId} onClick={() => onSelect(r.id)} />
      ))}
      {nextCursor && (
        <div style={{ padding: 12, display: "flex", justifyContent: "center" }}>
          <HqButton variant="secondary" size="sm" onClick={onLoadMore}>
            Load more
          </HqButton>
        </div>
      )}
    </div>
  );
}

function RailRow({
  row,
  active,
  onClick,
}: {
  row: FeedbackRow;
  active: boolean;
  onClick: () => void;
}) {
  const isNew = row.status === "new";
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        display: "flex",
        gap: 10,
        width: "100%",
        padding: "12px 14px",
        borderLeft: active ? "2px solid var(--hq-accent)" : "2px solid transparent",
        background: active ? "var(--hq-bg-hover)" : "transparent",
        border: "none",
        cursor: "pointer",
        textAlign: "left",
        borderBottom: "1px solid var(--hq-border)",
        color: "var(--hq-text)",
      }}
      onMouseEnter={(e) => {
        if (!active)
          (e.currentTarget as HTMLButtonElement).style.background = "rgba(255,255,255,.02)";
      }}
      onMouseLeave={(e) => {
        if (!active) (e.currentTarget as HTMLButtonElement).style.background = "transparent";
      }}
    >
      <HqAvatar name={row.licenseId ?? "anon"} size={32} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 2 }}>
          {isNew && <HqStatusDot tone="accent" pulse size={6} />}
          <span
            style={{
              fontSize: 12.5,
              fontWeight: isNew ? 600 : 500,
              color: "var(--hq-text)",
              flex: 1,
              minWidth: 0,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            <MonoId value={row.licenseId ?? "anon"} short={12} color="var(--hq-text)" />
          </span>
          <span className="hq-mono" style={{ fontSize: 10, color: "var(--hq-text-dim)" }}>
            {fmtRelative(row.receivedAt)}
          </span>
        </div>
        <div
          style={{
            fontSize: 11.5,
            color: "var(--hq-text-muted)",
            lineHeight: 1.45,
            overflow: "hidden",
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
          }}
        >
          {clamp(row.body, 140)}
        </div>
        {(row.tags.length > 0 || row.hasScreenshot || row.redactedFields.length > 0) && (
          <div style={{ display: "flex", gap: 4, marginTop: 6, flexWrap: "wrap" }}>
            {row.tags.slice(0, 3).map((t) => (
              <HqBadge key={t} tone="neutral" size="sm">
                {t}
              </HqBadge>
            ))}
            {row.hasScreenshot && (
              <HqBadge tone="accent" size="sm">
                shot
              </HqBadge>
            )}
            {row.redactedFields.length > 0 && (
              <HqBadge tone="warn" size="sm">
                redacted
              </HqBadge>
            )}
          </div>
        )}
      </div>
    </button>
  );
}
